/* ZSISKA Homepage JavaScript - Fall Winter 2025 */

(function() {
  'use strict';
  
  // Feature Detection
  const supportsIntersectionObserver = 'IntersectionObserver' in window;
  const supportsRequestAnimationFrame = 'requestAnimationFrame' in window;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Utility Functions
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }
  
  // Hero Slideshow
  class HeroSlideshow {
    constructor(element) {
      this.slideshow = element;
      this.slides = element.querySelectorAll('.hero-slide');
      this.dots = element.querySelectorAll('.hero-slideshow__dot');
      this.prevButton = element.querySelector('.hero-slideshow__arrow--prev');
      this.nextButton = element.querySelector('.hero-slideshow__arrow--next');
      this.currentSlide = 0;
      this.autoRotate = element.dataset.autorotate === 'true';
      this.speed = parseInt(element.dataset.speed) || 5000;
      this.autoRotateTimer = null;
      
      this.init();
    }
    
    init() {
      if (this.slides.length <= 1) return;
      
      this.showSlide(0);
      this.bindEvents();
      
      if (this.autoRotate && !prefersReducedMotion) {
        this.startAutoRotate();
      }
    }
    
    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.prevSlide());
      }
      
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.nextSlide());
      }
      
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });
      
      // Pause on hover
      this.slideshow.addEventListener('mouseenter', () => this.stopAutoRotate());
      this.slideshow.addEventListener('mouseleave', () => {
        if (this.autoRotate && !prefersReducedMotion) {
          this.startAutoRotate();
        }
      });
      
      // Pause on focus
      this.slideshow.addEventListener('focusin', () => this.stopAutoRotate());
      this.slideshow.addEventListener('focusout', () => {
        if (this.autoRotate && !prefersReducedMotion) {
          this.startAutoRotate();
        }
      });
      
      // Keyboard navigation
      this.slideshow.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.nextSlide();
        }
      });
    }
    
    showSlide(index) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('hero-slide--active', i === index);
      });
      
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('hero-slideshow__dot--active', i === index);
      });
      
      this.currentSlide = index;
    }
    
    nextSlide() {
      const nextIndex = (this.currentSlide + 1) % this.slides.length;
      this.showSlide(nextIndex);
    }
    
    prevSlide() {
      const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.showSlide(prevIndex);
    }
    
    goToSlide(index) {
      this.showSlide(index);
    }
    
    startAutoRotate() {
      this.stopAutoRotate();
      this.autoRotateTimer = setInterval(() => {
        this.nextSlide();
      }, this.speed);
    }
    
    stopAutoRotate() {
      if (this.autoRotateTimer) {
        clearInterval(this.autoRotateTimer);
        this.autoRotateTimer = null;
      }
    }
  }
  
  // Bestsellers Carousel
  class BestsellersCarousel {
    constructor(element) {
      this.carousel = element;
      this.track = element.querySelector('.bestsellers-track');
      this.prevButton = element.querySelector('.bestsellers-arrow--prev');
      this.nextButton = element.querySelector('.bestsellers-arrow--next');
      this.items = element.querySelectorAll('.bestseller-card');
      this.itemWidth = 300;
      this.visibleItems = this.getVisibleItems();
      
      this.init();
    }
    
    init() {
      this.bindEvents();
      this.updateButtons();
      
      // Handle resize
      window.addEventListener('resize', debounce(() => {
        this.visibleItems = this.getVisibleItems();
        this.updateButtons();
      }, 250));
    }
    
    getVisibleItems() {
      const containerWidth = this.carousel.offsetWidth;
      return Math.floor(containerWidth / this.itemWidth);
    }
    
    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollPrev());
      }
      
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollNext());
      }
      
      // Track scroll for button states
      this.track.addEventListener('scroll', throttle(() => {
        this.updateButtons();
      }, 100));
    }
    
    scrollPrev() {
      const scrollAmount = this.itemWidth * this.visibleItems;
      this.track.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
    
    scrollNext() {
      const scrollAmount = this.itemWidth * this.visibleItems;
      this.track.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
    
    updateButtons() {
      if (!this.prevButton || !this.nextButton) return;
      
      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.offsetWidth;
      
      this.prevButton.disabled = scrollLeft <= 0;
      this.nextButton.disabled = scrollLeft >= maxScroll - 1;
      
      this.prevButton.style.opacity = this.prevButton.disabled ? '0.5' : '1';
      this.nextButton.style.opacity = this.nextButton.disabled ? '0.5' : '1';
    }
  }
  
  // Intersection Observer for Animations
  function initScrollAnimations() {
    if (!supportsIntersectionObserver || prefersReducedMotion) return;
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    }, observerOptions);
    
    // Observe elements with animation classes
    const animatedElements = document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
    animatedElements.forEach(el => observer.observe(el));
  }
  
  // Smooth Scrolling for Anchor Links
  function initSmoothScrolling() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    
    anchorLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        const target = document.querySelector(href);
        
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }
  
  // Newsletter Form Enhancement
  function initNewsletterForm() {
    const forms = document.querySelectorAll('.newsletter-form');
    
    forms.forEach(form => {
      const input = form.querySelector('.newsletter-form__input');
      const button = form.querySelector('.newsletter-form__button');
      
      if (!input || !button) return;
      
      form.addEventListener('submit', (e) => {
        const email = input.value.trim();
        
        if (!email || !isValidEmail(email)) {
          e.preventDefault();
          showFormMessage(form, 'Please enter a valid email address.', 'error');
          return;
        }
        
        button.textContent = 'Subscribing...';
        button.disabled = true;
      });
    });
  }
  
  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  function showFormMessage(form, message, type) {
    let messageEl = form.querySelector('.newsletter-form__message');
    
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'newsletter-form__message';
      form.appendChild(messageEl);
    }
    
    messageEl.className = `newsletter-form__message newsletter-form__message--${type}`;
    messageEl.textContent = message;
    messageEl.style.display = 'block';
    
    setTimeout(() => {
      messageEl.style.display = 'none';
    }, 5000);
  }
  
  // Product Card Hover Effects
  function initProductCardEffects() {
    const productCards = document.querySelectorAll('.bestseller-card, .collection-card, .product-type-card, .gift-card');
    
    productCards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        if (!prefersReducedMotion) {
          card.style.transform = 'translateY(-8px)';
        }
      });
      
      card.addEventListener('mouseleave', () => {
        card.style.transform = 'translateY(0)';
      });
    });
  }
  
  // Lazy Loading Enhancement
  function initLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported
      return;
    }
    
    // Fallback for browsers without native lazy loading
    const lazyImages = document.querySelectorAll('img[loading="lazy"]');
    
    if (supportsIntersectionObserver) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            img.classList.remove('lazy');
            imageObserver.unobserve(img);
          }
        });
      });
      
      lazyImages.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback: load all images immediately
      lazyImages.forEach(img => {
        img.src = img.dataset.src || img.src;
      });
    }
  }
  
  // Performance Monitoring
  function initPerformanceMonitoring() {
    if ('performance' in window && 'mark' in performance) {
      performance.mark('zsiska-homepage-js-start');
      
      window.addEventListener('load', () => {
        performance.mark('zsiska-homepage-js-complete');
        performance.measure('zsiska-homepage-js', 'zsiska-homepage-js-start', 'zsiska-homepage-js-complete');
        
        // Log to console in development
        if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify')) {
          console.log('ZSISKA Homepage Performance:', performance.getEntriesByName('zsiska-homepage-js')[0]);
        }
      });
    }
  }
  
  // Initialize Everything
  function init() {
    // Core functionality
    initSmoothScrolling();
    initNewsletterForm();
    initLazyLoading();
    initPerformanceMonitoring();
    
    // Enhanced interactions (only if motion not reduced)
    if (!prefersReducedMotion) {
      initScrollAnimations();
      initProductCardEffects();
    }
    
    // Initialize components
    const heroSlideshows = document.querySelectorAll('.hero-slideshow');
    heroSlideshows.forEach(slideshow => new HeroSlideshow(slideshow));
    
    const bestsellersCarousels = document.querySelectorAll('.bestsellers-carousel');
    bestsellersCarousels.forEach(carousel => new BestsellersCarousel(carousel));
    
    // Mark as ready
    document.body.classList.add('zsiska-home-ready');
    
    console.log('🎨 ZSISKA Homepage Enhanced - Fall Winter 2025');
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Export for potential external use
  window.ZsiskaHome = {
    HeroSlideshow,
    BestsellersCarousel,
    initScrollAnimations,
    initSmoothScrolling
  };
  
})();
